import React from "react";
import LegalPage, { Section, Bullets } from "@/components/LegalPage";

export default function FAQ() {
  return (
    <LegalPage
      eyebrow="Support"
      title="FAQ & Refund Policy"
      intro="Everything you might want to know about receiving, reading, printing and — if it comes to it — returning your copy of Mizizi. If your question isn't here, write us at hello@mizizitable.com and we'll help."
      updated="February 2026"
    >
      <Section title="Digital Product Access">
        <p>
          Mizizi is a fully digital cookbook. When your purchase is confirmed we
          send a signed download link and a magic-link login to the email you
          used at checkout.
        </p>
        <Bullets items={[
          "Download-ready PDFs are provided in two variants: a print-friendly A4 PDF and a mobile-optimised PDF for reading on your phone or tablet.",
          "You can also read the book in your browser via the online Reader — sign in with the magic link we send to your inbox.",
          "You can request a fresh download link at any time by writing to hello@mizizitable.com from the email address you purchased with.",
          "If a download does not start, first check your spam/junk folder for the confirmation email, then try opening the link in a different browser. Still stuck? Email us with your order reference and we'll re-send it manually.",
        ]}/>
      </Section>

      <Section title="Refund Policy">
        <p>
          Because Mizizi is a digital product that can be downloaded and kept
          immediately, refunds are limited but always considered fairly.
        </p>
        <Bullets items={[
          "A refund may be requested within 7 days of purchase if the file has not yet been downloaded.",
          "Duplicate purchases (for example an accidental second checkout on the same email) will be reviewed and refunded where appropriate.",
          "If you're having trouble accessing your download, please reach out first — most access issues can be solved in a single email and we'd rather help you read the book than refund it.",
          "All refund requests should be sent to hello@mizizitable.com with your order reference. We aim to reply within 1–2 business days.",
        ]}/>
        <p className="italic text-mizizi-charcoal/70">
          We do not automatically track whether individual pages have been opened
          inside the Reader — refunds are handled by hand, in good faith, on a
          case-by-case basis.
        </p>
      </Section>

      <Section title="Printing & Personal Use">
        <p>
          Your purchase includes a print-ready PDF designed for personal use.
        </p>
        <Bullets items={[
          "You may print one personal copy of the cookbook for your own kitchen.",
          "The cookbook may not be redistributed, resold, copied, publicly uploaded, forwarded to friends, or shared in any file-sharing forum.",
          "The recipes, photography, layout and typography are the intellectual property of Mizizi and are provided under a personal-use licence only.",
        ]}/>
      </Section>

      <Section title="Allergies & Dietary Information">
        <Bullets items={[
          "All Mizizi recipes are 100% plant-based — no meat, dairy, eggs or honey. Substitutions in the book are suggestions and should be evaluated based on your own diet, allergies and preferences.",
          "Common allergens such as peanuts, tree nuts, sesame and soy appear in some recipes; each recipe lists its ingredients clearly and calls out obvious allergens where present.",
          "Please read every ingredient list carefully and consult a qualified medical or nutritional professional for personalised dietary advice. Nothing in the cookbook is intended as medical or nutritional advice.",
        ]}/>
      </Section>

      <Section title="Customer Support">
        <p>
          For anything at all — access, refunds, wholesale, press, or a recipe
          question — write to us at{" "}
          <a href="mailto:hello@mizizitable.com" className="text-mizizi-terracotta underline underline-offset-4 hover:text-mizizi-gold">
            hello@mizizitable.com
          </a>
          . We aim to reply within <strong>1–2 business days</strong>.
        </p>
      </Section>

      <Section title="Privacy & Customer Data">
        <Bullets items={[
          "We use your name, email and (when provided) phone number only to fulfil your purchase and to send you the book, order updates, and — if you opt in — occasional messages from Mizizi.",
          "Every marketing email includes a one-click unsubscribe link.",
          "Payment information is handled directly by our payment providers (Stripe for international cards; Paystack for M-PESA and cards in Kenya). We do not receive, view or store your card details on the Mizizi servers.",
          "For the full details please read our Privacy Policy.",
        ]}/>
      </Section>
    </LegalPage>
  );
}
